import { useState, useEffect } from 'react';
import '../styles/GradeModal.css';
import GradeFormatFields from './GradeFormatFields';
import { normalizeGradingMode } from '../utils/gradeValues';
import { extractErrorMessage, getFetchErrorMessage } from '../utils/errorUtils';

const GradeModal = ({ onClose }) => {
    // Step state: 'type' or 'form'
    const [step, setStep] = useState('type');
    const [selectedExamType, setSelectedExamType] = useState('');
    
    const [formData, setFormData] = useState({
        student_id: '',
        module_id: '',
        exam_date: '',
        grade: '',
        grade_format: 'scale_5',
        attempt: 1,
        exam_status: 'FAILED',
        group_assignment_id: '', // For assignments
        feedback: '', // For assignments
        activity_type: '', // For activities
        weight: 1.0, // For activities
        description: '' // For activities
    });
    
    // State to hold modules select
    const [modules, setModules] = useState([]);
    // State to hold students select
    const [students, setStudents] = useState([]);
    // State to hold assignments select (for assignment grading)
    const [assignments, setAssignments] = useState([]);
    // State for loading
    const [loading, setLoading] = useState(true);
    const [submitError, setSubmitError] = useState('');

    // Grade types available
    const gradeTypes = [
        { value: 'FINAL', label: 'Egzamin końcowy', description: 'Egzamin końcowy z przedmiotu', type: 'exam' },
        { value: 'RESIT', label: 'Egzamin poprawkowy', description: 'Egzamin poprawkowy', type: 'exam' },
        { value: 'ASSIGNMENT', label: 'Zadanie', description: 'Ocena z zadania', type: 'assignment' },
        { value: 'ACTIVITY', label: 'Inna aktywność', description: 'Prezentacja, test, quiz, itp.', type: 'activity' }
    ];

    // Activity types available
    const activityTypes = [
        { value: 'PRESENTATION', label: 'Prezentacja' },
        { value: 'QUIZ', label: 'Quiz' },
        { value: 'ACTIVITY', label: 'Aktywność' },
        { value: 'TEST', label: 'Test' },
        { value: 'OTHER', label: 'Inne' }
    ];

    // Exam status options - removed PENDING, added EXEMPT
    const examStatuses = [
        { value: 'PASSED', label: 'Zaliczony' },
        { value: 'FAILED', label: 'Niezaliczony' },
        { value: 'ABSENT', label: 'Nieobecny' },
        { value: 'EXEMPT', label: 'Zwolniony' }
    ];

    useEffect(() => {
        async function fetchModules() {
            try {
                const token = localStorage.getItem("token");

                const response = await fetch(`${process.env.REACT_APP_API_URL}/lecturer/grades/modules`, {
                    method: "GET",
                    headers: {
                        "Authorization": `Bearer ${token}`
                    }
                });

                if (response.ok) {
                    const data = await response.json();
                    setModules(data);
                } else {
                    console.error("Error fetching modules:", response.statusText);
                }
            } catch (error) {
                console.error("Error fetching modules:", error);
            } finally {
                setLoading(false);
            }
        }
        
        async function fetchStudents() {
            setLoading(false);
        }

        fetchModules();
        fetchStudents();
    }, []);

    const handleExamTypeSelect = (gradeType) => {
        setSelectedExamType(gradeType);
        setStep('form');
    };

    // Get the selected grade type details
    const selectedGradeType = gradeTypes.find(t => t.value === selectedExamType);
    const isAssignment = selectedGradeType?.type === 'assignment';
    const isActivity = selectedGradeType?.type === 'activity';

    // Fetch assignments when module is selected (for assignment grading)
    useEffect(() => {
        async function fetchAssignments() {
            if (!formData.module_id || !isAssignment) {
                setAssignments([]);
                return;
            }

            try {
                const token = localStorage.getItem("token");
                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/lecturer/grades/assignments?module_id=${formData.module_id}`,
                    {
                        method: "GET",
                        headers: {
                            "Authorization": `Bearer ${token}`
                        }
                    }
                );

                if (response.ok) {
                    const data = await response.json();
                    setAssignments(data);
                } else {
                    console.error("Error fetching assignments:", response.statusText);
                    setAssignments([]);
                }
            } catch (error) {
                console.error("Error fetching assignments:", error);
                setAssignments([]);
            }
        }

        if (step === 'form' && isAssignment) {
            fetchAssignments();
        }
    }, [formData.module_id, isAssignment, step]);

    // Fetch students when module/assignment/activity is selected (different logic for each type)
    useEffect(() => {
        async function fetchStudents() {
            if (!formData.module_id || !selectedExamType) {
                setStudents([]);
                return;
            }

            // For assignments, also need group_assignment_id
            if (isAssignment && !formData.group_assignment_id) {
                setStudents([]);
                return;
            }

            // For activities, also need activity_type
            if (isActivity && !formData.activity_type) {
                setStudents([]);
                return;
            }

            try {
                const token = localStorage.getItem("token");
                let url;
                
                if (isAssignment) {
                    url = `${process.env.REACT_APP_API_URL}/lecturer/grades/studentsWithoutAssignmentGrade?group_assignment_id=${formData.group_assignment_id}`;
                } else if (isActivity) {
                    url = `${process.env.REACT_APP_API_URL}/lecturer/grades/studentsWithoutActivityGrade?module_id=${formData.module_id}&activity_type=${formData.activity_type}`;
                } else {
                    url = `${process.env.REACT_APP_API_URL}/lecturer/grades/studentsWithoutGrade?module_id=${formData.module_id}&exam_type=${selectedExamType}`;
                }

                const response = await fetch(url, {
                    method: "GET",
                    headers: {
                        "Authorization": `Bearer ${token}`
                    }
                });

                if (response.ok) {
                    const data = await response.json();
                    setStudents(data);
                } else {
                    console.error("Error fetching students:", response.statusText);
                    setStudents([]);
                }
            } catch (error) {
                console.error("Error fetching students:", error);
                setStudents([]);
            }
        }

        if (step === 'form') {
            fetchStudents();
        }
    }, [formData.module_id, formData.group_assignment_id, formData.activity_type, selectedExamType, isAssignment, isActivity, step]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => {
            const newData = {
                ...prev,
                [name]: value
            };
            
            // Reset student_id when module, assignment, or activity type changes
            if (name === 'module_id' || name === 'group_assignment_id' || name === 'activity_type') {
                newData.student_id = '';
            }
            
            // Auto-update exam_status when grade changes (if grade is numeric)
            if (name === 'grade_format') {
                newData.grade = '';
            }

            if (name === 'grade' && value) {
                const gradingMode = normalizeGradingMode(newData.grade_format);
                if (gradingMode === 'pass_fail') {
                    if (value === 'PASSED') {
                        newData.exam_status = 'PASSED';
                    } else if (value === 'FAILED') {
                        newData.exam_status = 'FAILED';
                    }
                } else {
                const gradeNum = parseFloat(value);
                if (!isNaN(gradeNum)) {
                    // Only auto-update if status is FAILED (default), don't override ABSENT or EXEMPT
                    if (prev.exam_status === 'FAILED' || !prev.exam_status) {
                        if (gradingMode === 'percent') {
                            newData.exam_status = gradeNum >= 60 ? 'PASSED' : 'FAILED';
                        } else if (gradeNum >= 3.0) {
                            newData.exam_status = 'PASSED';
                        } else if (gradeNum > 0) {
                            newData.exam_status = 'FAILED';
                        }
                    }
                }
                }
            }
            
            return newData;
        });
    };

    const handleBack = () => {
        setStep('type');
        setSelectedExamType('');
        setSubmitError('');
        setFormData({
            student_id: '',
            module_id: '',
            exam_date: '',
            grade: '',
            grade_format: 'scale_5',
            attempt: 1,
            exam_status: 'FAILED',
            group_assignment_id: '',
            feedback: '',
            activity_type: '',
            weight: 1.0,
            description: ''
        });
        setStudents([]); // Clear students when going back
        setAssignments([]); // Clear assignments when going back
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitError('');

        if (isAssignment) {
            if (!formData.student_id || !formData.group_assignment_id || !formData.grade) {
                setSubmitError('Wypełnij wszystkie wymagane pola.');
                return;
            }
        } else if (isActivity) {
            if (!formData.student_id || !formData.module_id || !formData.activity_type || !formData.grade) {
                setSubmitError('Wypełnij wszystkie wymagane pola.');
                return;
            }
        } else {
            if (!formData.student_id || !formData.module_id || !formData.exam_date || !formData.exam_status) {
                setSubmitError('Wypełnij wszystkie wymagane pola.');
                return;
            }

            if (!formData.grade && formData.exam_status !== 'ABSENT' && formData.exam_status !== 'EXEMPT') {
                setSubmitError("Wybierz ocenę lub ustaw status „Nieobecny” / „Zwolniony”.");
                return;
            }
        }

        try {
            const token = localStorage.getItem("token");
            let url, body;

            if (isAssignment) {
                url = `${process.env.REACT_APP_API_URL}/lecturer/grades/addAssignment`;
                body = {
                    student_id: formData.student_id,
                    group_assignment_id: formData.group_assignment_id,
                    grade: formData.grade,
                    grade_format: formData.grade_format,
                    feedback: formData.feedback || null
                };
            } else if (isActivity) {
                url = `${process.env.REACT_APP_API_URL}/lecturer/grades/addActivity`;
                body = {
                    student_id: formData.student_id,
                    module_id: formData.module_id,
                    activity_type: formData.activity_type,
                    grade: formData.grade,
                    grade_format: formData.grade_format,
                    weight: parseFloat(formData.weight) || 1.0,
                    description: formData.description || null
                };
            } else {
                url = `${process.env.REACT_APP_API_URL}/lecturer/grades/addExam`;
                body = {
                    student_id: formData.student_id,
                    module_id: formData.module_id,
                    exam_date: formData.exam_date,
                    grade: formData.grade || null,
                    grade_format: formData.grade_format,
                    exam_type: selectedExamType,
                    attempt: parseInt(formData.attempt) || 1,
                    exam_status: formData.exam_status
                };
            }

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify(body)
            });

            if (!response.ok) {
                const msg = await getFetchErrorMessage(response, 'Nie udało się zapisać oceny.');
                throw new Error(msg);
            }

            // Reset form
            setFormData({
                student_id: '',
                module_id: '',
                exam_date: '',
                grade: '',
                attempt: 1,
                exam_status: 'FAILED',
                group_assignment_id: '',
                feedback: '',
                activity_type: '',
                weight: 1.0,
                description: ''
            });
            setSelectedExamType('');
            setStep('type');
            onClose();
        } catch (error) {
            console.error("Błąd podczas zapisu:", error);
            setSubmitError(extractErrorMessage(error));
        }
    }

    if (loading && step === 'type') {
        return (
            <div className="modal-overlay">
                <div className="modal-content">
                    <p>Ładowanie danych...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="modal-overlay">
            <div className="modal-content">
                {step === 'type' ? (
                    <>
                        <div className="exam-type-header">
                            <h3 className="modal-title">Wybierz typ oceny</h3>
                            <button 
                                type="button" 
                                className="modal-close-button" 
                                onClick={onClose}
                                title="Zamknij"
                            >
                                ×
                            </button>
                        </div>
                        <div className="exam-type-selection">
                            {gradeTypes.map((type) => (
                                <div
                                    key={type.value}
                                    className={`exam-type-card ${selectedExamType === type.value ? 'exam-type-card-active' : ''}`}
                                    onClick={() => handleExamTypeSelect(type.value)}
                                >
                                    <div className="exam-type-icon">
                                        <span>{type.label.charAt(0)}</span>
                                    </div>
                                    <h4 className="exam-type-name">{type.label}</h4>
                                    <p className="exam-type-description">{type.description}</p>
                                </div>
                            ))}
                        </div>
                    </>
                ) : (
                    <>
                        <div className="exam-form-header">
                            <button 
                                type="button" 
                                className="back-button" 
                                onClick={handleBack}
                                title="Wróć do wyboru typu"
                            >
                                ←
                            </button>
                            <h3 className="modal-title">
                                Dodaj ocenę: {gradeTypes.find(t => t.value === selectedExamType)?.label}
                            </h3>
                            <button 
                                type="button" 
                                className="modal-close-button" 
                                onClick={onClose}
                                title="Zamknij"
                            >
                                ×
                            </button>
                        </div>
                        <form className="form-group" onSubmit={handleSubmit}>
                            <label>Wybierz przedmiot:</label>
                            <select
                                name='module_id'
                                className="form-input"
                                value={formData.module_id}
                                onChange={handleChange}
                                required
                            >
                                <option value="">-- Wybierz --</option>
                                {modules.map(module => (
                                    <option key={module.module_id} value={module.module_id}>
                                        {module.module_name}
                                    </option>
                                ))}
                            </select>

                            {isAssignment && (
                                <>
                                    <label>Wybierz zadanie:</label>
                                    <select
                                        name='group_assignment_id'
                                        className="form-input"
                                        value={formData.group_assignment_id}
                                        onChange={handleChange}
                                        required
                                        disabled={!formData.module_id || assignments.length === 0}
                                    >
                                        <option value="">
                                            {!formData.module_id 
                                                ? '-- Najpierw wybierz przedmiot --' 
                                                : assignments.length === 0
                                                ? '-- Brak zadań dla tego przedmiotu --'
                                                : '-- Wybierz --'}
                                        </option>
                                        {assignments.map(assignment => (
                                            <option key={assignment.group_assignments_id} value={assignment.group_assignments_id}>
                                                {assignment.assignment_title} - {assignment.group_name}
                                                {assignment.due_date ? ` (Termin: ${assignment.due_date})` : ''}
                                            </option>
                                        ))}
                                    </select>
                                </>
                            )}

                            {isActivity && (
                                <>
                                    <label>Wybierz typ aktywności:</label>
                                    <select
                                        name='activity_type'
                                        className="form-input"
                                        value={formData.activity_type}
                                        onChange={handleChange}
                                        required
                                        disabled={!formData.module_id}
                                    >
                                        <option value="">
                                            {!formData.module_id 
                                                ? '-- Najpierw wybierz przedmiot --' 
                                                : '-- Wybierz --'}
                                        </option>
                                        {activityTypes.map(type => (
                                            <option key={type.value} value={type.value}>
                                                {type.label}
                                            </option>
                                        ))}
                                    </select>
                                </>
                            )}

                            <label>Wybierz kursanta:</label>
                            <select
                                name='student_id'
                                className="form-input"
                                value={formData.student_id}
                                onChange={handleChange}
                                required
                                disabled={
                                    !formData.module_id || 
                                    !selectedExamType || 
                                    (isAssignment && !formData.group_assignment_id) ||
                                    (isActivity && !formData.activity_type) ||
                                    students.length === 0
                                }
                            >
                                <option value="">
                                    {!formData.module_id 
                                        ? '-- Najpierw wybierz przedmiot --' 
                                        : isAssignment && !formData.group_assignment_id
                                        ? '-- Najpierw wybierz zadanie --'
                                        : isActivity && !formData.activity_type
                                        ? '-- Najpierw wybierz typ aktywności --'
                                        : !selectedExamType
                                        ? '-- Wybierz typ oceny --'
                                        : selectedExamType === 'RESIT' && students.length === 0
                                        ? '-- Brak kursantów do poprawy --'
                                        : selectedExamType === 'FINAL' && students.length === 0
                                        ? '-- Wszyscy kursanci mają już ocenę --'
                                        : isAssignment && students.length === 0
                                        ? '-- Wszyscy kursanci mają już ocenę z tego zadania --'
                                        : isActivity && students.length === 0
                                        ? '-- Wszyscy kursanci mają już ocenę z tej aktywności --'
                                        : '-- Wybierz --'}
                                </option>
                                {students.map(student => (
                                    <option key={student.student_id} value={student.student_id}>
                                        {student.first_name} {student.last_name}
                                        {student.group_name ? ` - ${student.group_name}` : ''}
                                        {selectedExamType === 'RESIT' && student.exam_status 
                                            ? ` (Poprzedni status: ${examStatuses.find(s => s.value === student.exam_status)?.label || student.exam_status})` 
                                            : ''}
                                    </option>
                                ))}
                            </select>

                            {!isAssignment && !isActivity && (
                                <label>Wybierz datę egzaminu:</label>
                            )}
                            {!isAssignment && !isActivity && (
                                <input
                                    type='date'
                                    name='exam_date'
                                    className="form-input"
                                    value={formData.exam_date}
                                    onChange={handleChange}
                                    required={!isAssignment && !isActivity}
                                />
                            )}

                            <label>
                                Ocena / wynik
                                {!isAssignment && !isActivity && (formData.exam_status === 'ABSENT' || formData.exam_status === 'EXEMPT') ? ' (opcjonalnie)' : ''}:
                            </label>
                            <GradeFormatFields
                                className="form-input"
                                format={formData.grade_format}
                                value={formData.grade}
                                onFormatChange={(next) => handleChange({ target: { name: 'grade_format', value: next } })}
                                onValueChange={(next) => handleChange({ target: { name: 'grade', value: next } })}
                            />

                            {!isAssignment && !isActivity && (
                                <div className="form-grid">
                                    <div className="form-group">
                                        <label>Numer próby:</label>
                                        <input
                                            type='number'
                                            name='attempt'
                                            className="form-input"
                                            value={formData.attempt}
                                            onChange={handleChange}
                                            min="1"
                                            required
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label>Status egzaminu:</label>
                                        <select
                                            name='exam_status'
                                            className="form-input"
                                            value={formData.exam_status}
                                            onChange={handleChange}
                                            required
                                        >
                                            {examStatuses.map(status => (
                                                <option key={status.value} value={status.value}>
                                                    {status.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            )}

                            {isAssignment && (
                                <div className="form-group">
                                    <label>Dodaj opinię (opcjonalnie):</label>
                                    <textarea
                                        name='feedback'
                                        className="form-input"
                                        value={formData.feedback}
                                        onChange={handleChange}
                                        rows="4"
                                        placeholder="Wpisz komentarz do oceny..."
                                    />
                                </div>
                            )}

                            {isActivity && (
                                <>
                                    <div className="form-grid">
                                        <div className="form-group">
                                            <label>Waga oceny (opcjonalnie):</label>
                                            <input
                                                type='number'
                                                name='weight'
                                                className="form-input"
                                                value={formData.weight}
                                                onChange={handleChange}
                                                min="0.01"
                                                max="10.00"
                                                step="0.01"
                                                placeholder="1.0"
                                            />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label>Opis (opcjonalnie):</label>
                                        <textarea
                                            name='description'
                                            className="form-input"
                                            value={formData.description}
                                            onChange={handleChange}
                                            rows="3"
                                            placeholder="Np. Prezentacja projektu, Quiz z lektury, itp."
                                        />
                                    </div>
                                </>
                            )}


                            {submitError && (
                                <div className="form-error-banner" role="alert">
                                    {submitError}
                                </div>
                            )}

                            <div className="report-modal-buttons">
                                <button 
                                    type="button" 
                                    className="report-modal-button report-modal-button-secondary" 
                                    onClick={onClose}
                                >
                                    Anuluj
                                </button>
                                <button 
                                    type="submit" 
                                    className="report-modal-button report-modal-button-primary"
                                >
                                    Zapisz
                                </button>
                            </div>
                        </form>
                    </>
                )}
            </div>
        </div>
    );
}

export default GradeModal;
